import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { AddContactInfoRequest } from '@/types/entities/contactInfo';
import { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import type { BeneficiaryMakeAClaimantRequest } from '@/domains/beneficiaries/types/api/requests/BeneficiaryMakeAClaimantRequest';
import { DeleteBeneficiaryRequest } from '../types/api/requests/DeleteBeneficiaryRequest';
import { initialBeneficiaryState } from './constants/beneficiarySliceConstants';

const beneficiarySlice = createSlice({
  name: 'beneficiarySlice',
  initialState: initialBeneficiaryState,
  reducers: {
    getBeneficiaryList: (state, { payload }: PayloadAction<{ claimId: string }>) => {
      state.beneficiaryListByClaimIdRequestsStates[payload.claimId] = {
        isLoading: true,
        isFetching: !state.beneficiaryListByClaimIdRequestsStates[payload.claimId],
        isError: false,
        isSuccess: false,
      };
    },
    getBeneficiaryListSuccess: (
      state,
      { payload }: PayloadAction<{ claimId: string; dataList: NormalizedV2Response[] }>,
    ) => {
      state.beneficiaryListByClaimId[payload.claimId] = payload.dataList;

      state.beneficiaryListByClaimIdRequestsStates[payload.claimId] = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    deleteBeneficiary: (_, _action: PayloadAction<DeleteBeneficiaryRequest>) => {},
    deleteBeneficiarySuccess: () => {},

    sendMakeAClaimant: (_, _action: PayloadAction<BeneficiaryMakeAClaimantRequest>) => {},
    sendMakeAClaimantSuccess: () => {},
    addBeneficiaryContactInfo: (_, _action: PayloadAction<AddContactInfoRequest>) => {},
    addBeneficiaryAddress: (_, _action: PayloadAction<AddAddressRequest>) => {},
    generateClaimantAccountPasscode: (_, _action: PayloadAction<{ beneficiaryId: number }>) => {},
    updateBeneficiary: (state, { payload }: PayloadAction<number | string>) => {
      state.updatedBeneficiaryRequestState = {
        id: payload,
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },

    updateBeneficiarySuccess: (state, { payload }: PayloadAction<NormalizedV2Response>) => {
      const beneficiaryId = String(payload.id);

      const claimId = Object.keys(state.beneficiaryListByClaimId).find((claimIdKey) =>
        state.beneficiaryListByClaimId[claimIdKey]?.some((beneficiary) => String(beneficiary.id) === beneficiaryId),
      );

      if (claimId) {
        const beneficiaryIndex = state.beneficiaryListByClaimId[claimId].findIndex(
          (beneficiary) => String(beneficiary.id) === beneficiaryId,
        );

        if (beneficiaryIndex !== -1) {
          state.beneficiaryListByClaimId[claimId][beneficiaryIndex] = payload;
        }
      }

      state.updatedBeneficiaryRequestState = {
        id: state.updatedBeneficiaryRequestState.id,
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },

    updateBeneficiaryFailed: (state) => {
      state.updatedBeneficiaryRequestState = {
        id: state.updatedBeneficiaryRequestState.id,
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
  },
});

export const {
  getBeneficiaryList,
  getBeneficiaryListSuccess,
  deleteBeneficiary,
  deleteBeneficiarySuccess,
  sendMakeAClaimant,
  sendMakeAClaimantSuccess,
  addBeneficiaryContactInfo,
  addBeneficiaryAddress,
  generateClaimantAccountPasscode,
  updateBeneficiary,
  updateBeneficiarySuccess,
  updateBeneficiaryFailed,
} = beneficiarySlice.actions;

export const beneficiarySliceReducerPath = beneficiarySlice.reducerPath;

export const beneficiarySliceReducer = beneficiarySlice.reducer;
